import type { TFunction } from "i18next";
import type {
  InProductionEntryData,
  InStockOrProductionEntryDataInCW,
  PackagingInformationEntry,
} from "~/domains/product/types/ProductData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { PackagingOption } from "~/domains/product/types/PackagingOption";
import { buildPackagingOptions, type SalePerPack } from "~/domains/product/utils/buildPackagingOptions";
import {
  buildEffectivePackaging,
  type PredecessorDescriptorWithEntries,
} from "~/domains/product/utils/buildEffectivePackaging";
import { getInStockAndInProduction } from "~/utils/packageEntryUtils";
import { uniqueValues } from "~/common/utils/misc";

type FifteenDigitKey = string;
type LookupTuple = [ProductDescriptor, PackagingInformationEntry];

function getTotalInStock(entry: PackagingInformationEntry) {
  return entry.totalWarehouse.inStock.reduce((sum, stockEntry) => sum + stockEntry.quantity, 0);
}

export { type PredecessorDescriptorWithEntries };

export function buildPackagingOptionsPhaseout(
  entries: PackagingInformationEntry[],
  descriptor: ProductDescriptor,
  salePerPack: SalePerPack,
  t: TFunction,
  predecessors: PredecessorDescriptorWithEntries[],
): PackagingOption[] {
  const lookup = new Map<FifteenDigitKey, LookupTuple>();

  for (const { descriptor: predDescriptor, entries: predEntries } of predecessors) {
    const sapMaterialNumber = predDescriptor.base.sapMaterialNumber;

    for (const predEntry of predEntries) {
      lookup.set(sapMaterialNumber + predEntry.packCode, [predDescriptor, predEntry]);
    }
  }

  const effectiveEntries = buildEffectivePackaging(entries, descriptor, predecessors);
  const effectiveByPackCode = effectiveEntries.reduce<Record<string, (typeof effectiveEntries)[number]>>(
    (acc, entry) => {
      acc[entry.packCode] = entry;
      return acc;
    },
    {},
  );
  const baseOptions = buildPackagingOptions(entries, descriptor, salePerPack, t);

  return baseOptions.flatMap((option) => {
    const effectiveEntry = effectiveByPackCode[option.packCode];
    if (!effectiveEntry) {
      return [];
    }

    const fifteenDigitEntry = descriptor.base.fifteenDigit?.[option.packCode];
    const predecessorEntries = (fifteenDigitEntry?.predecessors ?? [])
      .flatMap((materialNo) => {
        const predecessorMatch = lookup.get(materialNo);

        if (!predecessorMatch) {
          return [];
        }

        const [, predEntry] = predecessorMatch;

        return {
          predEntry,
          totalStock: getInStockAndInProduction(predEntry),
          materialNo: materialNo,
        };
      })
      .filter((entry) => entry.totalStock > 0);

    // Sort by package size only
    const sortedPredecessors = [...predecessorEntries].sort((left, right) => {
      let result = right.predEntry.packSize - left.predEntry.packSize;
      if (result === 0) {
        result = right.totalStock - left.totalStock;
      }
      return result;
    });

    const mainProductStock = option.stockLevels.inStock.filter((entry) => !entry.sale);
    const additionalInStock = option.stockLevels.inStock.filter((entry) => entry.sale);

    const predecessorInClarifications: number[] = [];
    const predecessorInProduction: InProductionEntryData[] = [];
    const predecessorInProductionWeekly: InStockOrProductionEntryDataInCW[] = [];
    const predecessorInStockWeekly: InStockOrProductionEntryDataInCW[] = [];
    const sortedSapNumbers: string[] = uniqueValues(sortedPredecessors.map((entry) => entry.materialNo));
    for (const { predEntry } of sortedPredecessors) {
      additionalInStock.push(...predEntry.totalWarehouse.inStock);
      predecessorInClarifications.push(predEntry.totalWarehouse.inClarification);
      predecessorInProduction.push(...predEntry.totalWarehouse.inProduction);
      predecessorInProductionWeekly.push(...predEntry.totalWarehouse.inProductionWeekly);
      predecessorInStockWeekly.push(...predEntry.totalWarehouse.inStockWeekly);
    }

    additionalInStock.push(...mainProductStock);

    const hasCustomLabel = effectiveEntry.hasMixedPackSizes || effectiveEntry.predecessors.length > 0;

    const sortedPredecessorProducts = [...effectiveEntry.predecessors].sort(
      (left, right) => sortedSapNumbers.indexOf(left.sapMaterialNo) - sortedSapNumbers.indexOf(right.sapMaterialNo),
    );

    return [
      {
        ...option,
        ean: effectiveEntry.ean,
        aliases: effectiveEntry.aliases,
        countriesOfOrigin: effectiveEntry.countriesOfOrigin,
        customLabel: hasCustomLabel ? t("product.phaseOut.mixedPackages") : undefined,
        predecessors: sortedPredecessorProducts,
        showContactSupport: predecessorEntries.length > 0 ? true : undefined,
        packSize: effectiveEntry.packSize,
        stockLevels: {
          ...option.stockLevels,
          inStock: additionalInStock,
          inStockWeekly: [...predecessorInStockWeekly, ...option.stockLevels.inStockWeekly],
          inProduction: [...predecessorInProduction, ...option.stockLevels.inProduction],
          inProductionWeekly: [...predecessorInProductionWeekly, ...option.stockLevels.inProductionWeekly],
          inClarification: [...predecessorInClarifications, ...option.stockLevels.inClarification],
        },
      },
    ];
  });
}
